import { Alert, Anchor, Badge, Group, Stack, Text } from '@mantine/core'
import { IconAlertCircle, IconExternalLink } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'

function publicationCopy(publication, t) {
  if (publication.status === 'draft') {
    return {
      color: 'blue',
      label: t('editorV2.shell.publication.draft'),
      description: t('editorV2.shell.publication.draftDescription', { id: publication.databaseAdId }),
    }
  }
  if (publication.status === 'error') {
    return {
      color: 'red',
      label: t('editorV2.shell.publication.error'),
      description: publication.message
        || t('editorV2.shell.publication.errorDescription'),
    }
  }

  if (!publication.formatCompatible) {
    return {
      color: 'orange',
      label: t('editorV2.shell.publication.formatLocked'),
      description: t('editorV2.shell.publication.formatLockedDescription', {
        id: publication.databaseAdId,
        width: publication.format?.width,
        height: publication.format?.height,
      }),
    }
  }

  if (publication.status === 'pending') {
    return {
      color: 'orange',
      label: t('editorV2.shell.publication.pending'),
      description: t('editorV2.shell.publication.pendingDescription', { id: publication.databaseAdId }),
    }
  }

  if (!publication.upToDate) {
    return {
      color: 'blue',
      label: t('editorV2.shell.publication.outdated'),
      description: t('editorV2.shell.publication.outdatedDescription', {
        id: publication.databaseAdId,
        revision: publication.publishedRevision,
      }),
    }
  }

  return {
    color: 'green',
    label: t('editorV2.shell.publication.published'),
    description: t('editorV2.shell.publication.publishedDescription', {
      id: publication.databaseAdId,
      revision: publication.publishedRevision,
    }),
  }
}

/**
 * Read-only publication status. The "Publish as draft ad" button was removed from the
 * editor (2026-09-06); publishing stays an API contract (`POST /ads/{id}/publish`) that the
 * page-header flow will call. Saved drafts show their database ID and list availability.
 */
export function EditorPublishAction({ publication }) {
  const { t } = useTranslation()
  if (!publication?.databaseAdId) return null

  const copy = publicationCopy(publication, t)

  return (
    <Stack gap="xs">
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <div>
          <Group gap={6}>
            <Text size="sm" fw={650}>{publication.status === 'draft' ? t('editorV2.shell.publication.savedAd') : t('editorV2.shell.publication.service')}</Text>
            <Badge size="xs" variant="light" color={copy.color}>{copy.label}</Badge>
          </Group>
          <Text size="xs" c="dimmed" mt={2}>{copy.description}</Text>
        </div>
      </Group>

      {publication.status === 'error' && (
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertCircle size={16} />}
          py="xs"
        >
          {publication.message}
        </Alert>
      )}

      {publication.entryHtmlUrl && (
        <Anchor
          href={publication.entryHtmlUrl}
          target="_blank"
          rel="noreferrer"
          size="xs"
          c="dimmed"
          inline
        >
          {t('editorV2.shell.publication.openHtml')} <IconExternalLink size={12} style={{ verticalAlign: -2 }} />
        </Anchor>
      )}
    </Stack>
  )
}
